import { useQuery, useReactiveVar } from "@apollo/client";

import {
  EPermissionActions,
  IOrganizationParams,
  IPropertyData,
  IUserData,
} from "../../interfaces/interfaces";
import { organizationIdNameVar } from "../apollo/LocalState";
import DropDownMenu from "../common/DropDownMenu";
import { ReactComponent as Key } from "../../img/key.svg";
import { ReactComponent as Properties } from "../../img/Properties.svg";
import { ReactComponent as More } from "../../img/header/More.svg";
import { EPermissions, hasPermission } from "../../utils/rolesAndPermissions";
import { RenderEmptyHomeView } from "../../utils/utils";
import { GET_ORGANIZATION_SUMMARY } from "../../api/gqlQueries";
import Spinner from "../Spinner";

import Menu from "./Menu";
import BuildingCard from "./BuildingCard";
import SettingsCard from "./SettingsCard";

interface IOrganizationData {
  organization: {
    numHomes: number;
    buildings: IPropertyData[];
  };
}

const OrganizationDetailsBCC: React.FC<{
  userData: IUserData;
  setShowEditOrganization: React.Dispatch<React.SetStateAction<boolean>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  dropDownMenuVisible: boolean;
  setDropDownMenuVisible: React.Dispatch<React.SetStateAction<boolean>>;
  organizationId: string;
}> = ({
  userData,
  setShowEditOrganization,
  setShowModal,
  dropDownMenuVisible,
  setDropDownMenuVisible,
  organizationId,
}) => {
  const organizationIdName = useReactiveVar(organizationIdNameVar);
  const { loading, data } = useQuery<IOrganizationData, IOrganizationParams>(
    GET_ORGANIZATION_SUMMARY,
    {
      variables: { organizationId },
    }
  );
  if (loading) return <Spinner />;
  if (!data) throw Error("No data was returned");
  const { buildings, numHomes } = data.organization;
  const numBuildings = buildings.length;
  return (
    <div>
      <div className="dashboard-settings-container">
        <div>
          <div className="unit-title dashboard-settings-title">
            {organizationIdName.organizationName}
          </div>
        </div>
        <div className="dashboard-settings-container-inner">
          <Menu
            icon={Properties}
            amount={numBuildings}
            name="Properties"
            singular="Property"
          />
          <Menu
            icon={Key}
            amount={numHomes}
            name="Rental Units"
            singular="Rental Unit"
          />
        </div>
        <div className="dashboard-settings-more">
          {dropDownMenuVisible && (
            <DropDownMenu>
              <button
                className="dashboard-settings-more-btn unit-description-small"
                type="button"
                onClick={() => setShowEditOrganization(true)}
              >
                Edit Organization
              </button>
            </DropDownMenu>
          )}
          {hasPermission(
            userData.user.role,
            EPermissions.ORGANIZATIONS,
            EPermissionActions.UPDATE
          ) && (
            <More
              className="dashboard-settings-more-svg"
              onClick={() => setDropDownMenuVisible(!dropDownMenuVisible)}
              title="More Button"
            />
          )}
        </div>
      </div>
      <div className="options">
        <div className="dashboard-name-container">
          <div className="options-name menu">Properties</div>
          {hasPermission(
            userData.user.role,
            EPermissions.BUILDINGS,
            EPermissionActions.CREATE
          ) && (
            <button
              className="dashboard-name-button"
              type="button"
              data-testid="create-organization-btn"
              onClick={() => setShowModal(true)}
            >
              + Create Building
            </button>
          )}
        </div>
        {numBuildings === 0 ? (
          <RenderEmptyHomeView
            icon={Properties}
            iconClassName="dashboard-empty-img"
            iconTitle="Empty Properties"
            componentTitle="You haven't added any properties to Brilliant yet"
            componentDescription="To add a property, contact Brilliant or link your property management software (PMS) system."
          />
        ) : (
          <div className="options-properties">
            {buildings.map((building: IPropertyData) => {
              return <BuildingCard key={building.id} property={building} />;
            })}
          </div>
        )}

        <div className="options-name menu">Settings</div>
        <div className="options-properties">
          <SettingsCard
            link={`/${organizationId}/alertPreferences`}
            settingName="Alert Preferences"
          />
          {hasPermission(
            userData.user.role,
            EPermissions.CONFIGURATIONS,
            EPermissionActions.VIEW
          ) && (
            <SettingsCard
              link={`/${organizationId}/configurations`}
              settingName="Configurations"
            />
          )}
          <SettingsCard
            link={`/${organizationId}/managers`}
            settingName="Property Managers"
          />
          <SettingsCard
            link={`/${organizationId}/integrations`}
            settingName="Integrations"
          />
        </div>
      </div>
    </div>
  );
};

export default OrganizationDetailsBCC;
